import React from "react";
import { Search, Bookmark, BookmarkCheck, Send, X } from "lucide-react";

export function timeAgo(iso) {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

// Shortlisted / saved / contacted for one search — the same three numbers wherever a
// search is listed, so the row under the search bar and the panel read identically.
export const SearchCountChips = ({ search }) => (
    <div className="flex items-center gap-1.5 shrink-0">
        <span
            title="Shortlisted"
            className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-500/10 rounded-full px-2 py-0.5"
        >
            <Bookmark className="w-3 h-3" /> {search.shortlist_count ?? 0}
        </span>
        <span
            title="Saved"
            className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/40 rounded-full px-2 py-0.5"
        >
            <BookmarkCheck className="w-3 h-3" /> {search.saved_count ?? 0}
        </span>
        <span
            title="Contacted"
            className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/10 rounded-full px-2 py-0.5"
        >
            <Send className="w-3 h-3" /> {search.contacted_count ?? 0}
        </span>
    </div>
);

// Shared by every list of searches so a row is exactly 64px tall in all three states —
// real, loading, reserved. The inner column is 38px (20px title + 2px + 16px meta) plus
// py-3 and the 1px border; the skeleton below mirrors it bar for bar.
export const SEARCH_ROW_CLASS =
    "w-full flex items-center gap-3 text-left px-4 py-3 rounded-xl border transition-all";

export const SearchRowSkeleton = () => (
    <div className={`${SEARCH_ROW_CLASS} border-gray-100 dark:border-gray-700/30`} aria-hidden>
        <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-700/50 animate-pulse shrink-0" />
        <div className="min-w-0 flex-1">
            <div className="h-5 w-2/3 rounded bg-gray-100 dark:bg-gray-700/50 animate-pulse" />
            <div className="h-4 w-1/3 rounded bg-gray-100 dark:bg-gray-700/50 animate-pulse mt-0.5" />
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
            {[0, 1, 2].map(i => (
                <div key={i} className="w-9 h-5 rounded-full bg-gray-100 dark:bg-gray-700/50 animate-pulse" />
            ))}
        </div>
    </div>
);

// A row's worth of held space, for slots the data hasn't filled.
export const SearchRowPlaceholder = () => (
    <div className={`${SEARCH_ROW_CLASS} border-transparent invisible`} aria-hidden>
        <div className="h-[38px]" />
    </div>
);

// Every search the org has run under one job role, newest first — opens as a right-hand
// drawer, matching the candidate panel.
const RecentSearchesPanel = ({ searches = [], loading = false, jobRole = "", onClose, onOpenSearch }) => (
    <>
        <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
        <div className="fixed top-0 right-0 h-full w-[560px] bg-white dark:bg-[#1c1c1c] z-50 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                <div>
                    <h2 className="text-base font-semibold text-textPrimary dark:text-[#D9D9D9]">All searches</h2>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate max-w-[380px]">
                        {loading
                            ? "Loading…"
                            : [`${searches.length} ${searches.length === 1 ? "search" : "searches"}`, jobRole]
                                .filter(Boolean)
                                .join(" · ")}
                    </p>
                </div>
                <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
                {loading ? (
                    <div className="flex flex-col gap-2">
                        {[0, 1, 2, 3, 4].map(i => <SearchRowSkeleton key={i} />)}
                    </div>
                ) : searches.length === 0 ? (
                    <div className="flex flex-col items-center text-center py-20">
                        <Search className="w-9 h-9 text-gray-300 dark:text-gray-600 mb-3" />
                        <p className="text-sm font-medium text-textPrimary dark:text-[#D9D9D9]">No searches yet</p>
                        <p className="text-xs text-textSecondary mt-1">Run a search and it will show up here.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        {searches.map((s) => (
                            <button
                                key={s.search_id}
                                onClick={() => onOpenSearch(s)}
                                className="w-full text-left rounded-xl border border-gray-200 dark:border-gray-700/50 px-4 py-3 hover:border-purple-200 dark:hover:border-purple-500/40 hover:shadow-sm transition-all"
                            >
                                <div className="flex items-center gap-3">
                                    <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm text-textPrimary dark:text-[#D9D9D9] line-clamp-2">
                                            {s.alias || s.raw_query}
                                        </p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 truncate">
                                            {`${s.result_count ?? 0} candidates · ${timeAgo(s.created_at)}`}
                                        </p>
                                    </div>
                                    <SearchCountChips search={s} />
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    </>
);

export default RecentSearchesPanel;
